/**
 * The deployed `/build-manifest.json` — ONE parser, ONE URL builder.
 *
 * The Vite plugin writes `build/build-manifest.json` = `{ buildId, compatLevel }`
 * (apps/web/buildIdPlugin.js). What is on Hosting, its manifest says: the live
 * build id and the live compatibility level. Every reader — the client hook,
 * the server wrapper, the deploy scripts — decides what a response MEANS here,
 * so they cannot disagree about "no manifest yet" versus "cannot read it".
 * Unknown extra fields are ignored (the manifest is allowed to grow).
 */

/**
 * Classify a manifest response.
 *
 * `status` is the HTTP status, or `null` / `0` when there was no response at
 * all (network error or timeout) — the caller maps those to `null` / `0`
 * rather than throwing, so this stays a pure function of the response.
 *
 *   absent      a 404, or a 2xx that is not application/json (the `**` ->
 *               /index.html SPA fallback of firebase.json answers HTML for
 *               any unknown path). Means "no manifest has been deployed yet".
 *   unreadable  no response, any other non-2xx, JSON that does not parse, or
 *               JSON without a numeric `compatLevel` / a non-empty string
 *               `buildId`. Means "something is there but I cannot trust it".
 *   ok          everything else; `manifest` is `{ buildId, compatLevel }`.
 *
 * @param {{ status: number|null, contentType?: string|null, text?: string|null }} response
 * @returns {{ kind: 'absent'|'unreadable'|'ok', manifest: { buildId: string, compatLevel: number }|null }}
 */
export function parseBuildManifest({ status, contentType, text } = {}) {
  if (!Number.isFinite(status) || status === 0) return { kind: "unreadable", manifest: null };
  if (status === 404) return { kind: "absent", manifest: null };
  if (status < 200 || status > 299) return { kind: "unreadable", manifest: null };
  const isJson = typeof contentType === "string" && /^\s*application\/json\b/i.test(contentType);
  if (!isJson) return { kind: "absent", manifest: null };
  let data = null;
  try {
    data = JSON.parse(text);
  } catch (e) {
    return { kind: "unreadable", manifest: null };
  }
  if (!data || typeof data !== "object") return { kind: "unreadable", manifest: null };
  const level = data.compatLevel;
  if (typeof level !== "number" || !Number.isFinite(level)) return { kind: "unreadable", manifest: null };
  if (typeof data.buildId !== "string" || data.buildId === "") return { kind: "unreadable", manifest: null };
  return { kind: "ok", manifest: { buildId: data.buildId, compatLevel: level } };
}

/**
 * Where the deployed manifest lives: straight at Hosting, never through the
 * Cloudflare-fronted production domain (which may cache what it should not).
 */
export function liveManifestUrl(projectId) {
  return `https://${projectId}.web.app/build-manifest.json`;
}
